import { Receipt } from 'lucide-react';

import { humanizeLabel } from '@/nucleo/displayLabels';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { formatarData } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import { Card, CardContent } from '@/ui/base/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import { useDamspDoCredenciado, type CobrancaDamspDoCredenciado } from './detalheDados';

interface AbaDamspProps {
  credenciadoId: string;
}

const SELO_DA_COBRANCA: Record<CobrancaDamspDoCredenciado['status_cobranca'], StatusVariant> = {
  PENDENTE: 'pendente',
  EMITIDA: 'emitida',
  PAGA: 'pago',
  VENCIDA: 'bloqueado',
  CANCELADA: 'cancelado',
};

/**
 * Aba "DAMSP" (cobranças administrativas). SEM CLIQUE NA LINHA DE PROPOSITO: o
 * protótipo levava a `/cobrancas/:id`, rota que nao existe aqui — ver o mesmo
 * raciocinio em `AbaTermos`.
 */
export function AbaDamsp({ credenciadoId }: AbaDamspProps) {
  const { data: cobrancas, isLoading } = useDamspDoCredenciado(credenciadoId);

  return (
    <Card>
      <CardContent className="p-6">
        {isLoading ? (
          <Spinner size="sm" />
        ) : !cobrancas || cobrancas.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="Nenhuma cobrança DAMSP"
            description="Cobranças administrativas serão exibidas aqui."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>DAMSP</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cobrancas.map((cobranca) => (
                <TableRow key={cobranca.id}>
                  <TableCell className="font-mono">{cobranca.numero_damsp || '—'}</TableCell>
                  <TableCell>{humanizeLabel(cobranca.tipo_servico)}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{cobranca.descricao}</TableCell>
                  <TableCell>{formatarData(cobranca.data_vencimento)}</TableCell>
                  <TableCell>
                    <StatusBadge
                      status={SELO_DA_COBRANCA[cobranca.status_cobranca]}
                      label={cobranca.status_cobranca}
                    />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatarDinheiro(cobranca.valor)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
